import { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { parseUnits, maxUint256, formatUnits } from 'viem';
import { CONTRACT_ADDRESSES, ROUTER_ABI, PAIR_ABI, EMBER_TOKEN_ABI, MASTER_CHEF_ABI, FACTORY_ABI } from '../config/contracts';
import { BACKEND_URL } from '../config/api';
import { getTokenLogo } from '../config/tokens';
import LPRewardsPanel from './LPRewardsPanel';

const EXPLORER = 'https://flare-explorer.flare.network';

function LiquiditySection({ isConnected, selectedPool }) {
  const { address } = useAccount();
  const [tokens, setTokens] = useState([]);
  const [tokenA, setTokenA] = useState(null);
  const [tokenB, setTokenB] = useState(null);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [status, setStatus] = useState('');
  const [activePanel, setActivePanel] = useState('add');
  const [needsApprovalA, setNeedsApprovalA] = useState(false);
  const [needsApprovalB, setNeedsApprovalB] = useState(false);
  const [lpBalance, setLpBalance] = useState('0');
  const [lpToRemove, setLpToRemove] = useState('');
  const [needsLpApproval, setNeedsLpApproval] = useState(false);
  const [pairAddress, setPairAddress] = useState(null);
  const [reserves, setReserves] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentTxType, setCurrentTxType] = useState(null); // Track transaction type

  const { writeContract, data: txHash, isPending, error: writeError } = useWriteContract();
  const { isLoading: isConfirming, isSuccess, isError: isReceiptError, error: receiptError } = useWaitForTransactionReceipt({
    hash: txHash,
    timeout: 120_000, // 2 minutes – prevents UI locking up on slow/unreliable RPC
  });
  const notDeployed = !CONTRACT_ADDRESSES.ROUTER;

  // Check allowance for Token A
  const allowanceAQuery = useReadContract({
    address: tokenA?.flare_contract,
    abi: EMBER_TOKEN_ABI,
    functionName: 'allowance',
    args: (tokenA?.flare_contract && address) ? [address, CONTRACT_ADDRESSES.ROUTER] : undefined,
    query: {
      enabled: !!(tokenA?.flare_contract && address && CONTRACT_ADDRESSES.ROUTER),
      retry: false,
      gcTime: 0,
      staleTime: 0,
    },
  });

  const allowanceA = allowanceAQuery?.data;
  const refetchAllowanceA = allowanceAQuery?.refetch;

  // Check allowance for Token B
  const allowanceBQuery = useReadContract({
    address: tokenB?.flare_contract,
    abi: EMBER_TOKEN_ABI,
    functionName: 'allowance',
    args: (tokenB?.flare_contract && address) ? [address, CONTRACT_ADDRESSES.ROUTER] : undefined,
    query: {
      enabled: !!(tokenB?.flare_contract && address && CONTRACT_ADDRESSES.ROUTER),
      retry: false,
      gcTime: 0,
      staleTime: 0,
    },
  });

  const allowanceB = allowanceBQuery?.data;
  const refetchAllowanceB = allowanceBQuery?.refetch;

  // Read LP token balance for the selected pair
  const lpBalanceQuery = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!(address && pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000'),
      retry: false,
      gcTime: 0,
      staleTime: 0,
    },
  });

  const lpBalanceData = lpBalanceQuery?.data;
  const refetchLpBalance = lpBalanceQuery?.refetch;

  // Check LP token allowance for Router
  const lpAllowanceQuery = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'allowance',
    args: address ? [address, CONTRACT_ADDRESSES.ROUTER] : undefined,
    query: {
      enabled: !!(address && pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000' && CONTRACT_ADDRESSES.ROUTER),
      retry: false,
      gcTime: 0,
      staleTime: 0,
    },
  });

  const lpAllowance = lpAllowanceQuery?.data;
  const refetchLpAllowance = lpAllowanceQuery?.refetch;

  // Check if user has LP tokens staked in MasterChef (for EMBER/WFLR pool - pid 0)
  const { data: stakedLpInfo } = useReadContract({
    address: CONTRACT_ADDRESSES.MASTER_CHEF,
    abi: MASTER_CHEF_ABI,
    functionName: 'userInfo',
    args: (address && CONTRACT_ADDRESSES.MASTER_CHEF && pairAddress === CONTRACT_ADDRESSES.EMBER_WFLR_PAIR) ? [0, address] : undefined, // pid 0 is EMBER/WFLR pool
    query: {
      enabled: !!(address && CONTRACT_ADDRESSES.MASTER_CHEF && pairAddress === CONTRACT_ADDRESSES.EMBER_WFLR_PAIR),
      retry: 3,
      retryDelay: 1000,
      throwOnError: false,
    },
  });

  const stakedLpAmount = stakedLpInfo ? stakedLpInfo[0] : 0n;
  const hasStakedLp = stakedLpAmount > 0n && pairAddress === CONTRACT_ADDRESSES.EMBER_WFLR_PAIR;

  // Get pair address for the selected token pair
  const { data: pairAddressData } = useReadContract({
    address: CONTRACT_ADDRESSES.FACTORY,
    abi: FACTORY_ABI,
    functionName: 'getPair',
    args: (tokenA?.flare_contract && tokenB?.flare_contract)
      ? [tokenA.flare_contract, tokenB.flare_contract]
      : undefined,
    query: {
      enabled: !!(tokenA?.flare_contract && tokenB?.flare_contract && CONTRACT_ADDRESSES.FACTORY),
      retry: false,
    },
  });

  // Get reserves from the pair
  const { data: reservesData } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'getReserves',
    query: {
      enabled: !!(pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000'),
      retry: false,
      gcTime: 0,
      staleTime: 0,
    },
  });

  // Get token0 address to determine reserve ordering
  const { data: token0Address } = useReadContract({
    address: pairAddress,
    abi: PAIR_ABI,
    functionName: 'token0',
    query: {
      enabled: !!(pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000'),
      retry: false,
    },
  });

  useEffect(() => { loadTokens(); }, []);

  // Pre-select tokens when navigating from PoolsOverview
  useEffect(() => {
    if (selectedPool && tokens.length > 0) {
      const token0 = tokens.find(t => t.symbol === selectedPool.token0);
      const token1 = tokens.find(t => t.symbol === selectedPool.token1);

      if (token0) setTokenA(token0);
      if (token1) setTokenB(token1);
    }
  }, [selectedPool, tokens]);

  useEffect(() => {
    if (isSuccess) {
      setStatus('✅ Transaction confirmed!');

      // Only clear amounts for add/remove liquidity, not for approvals
      if (currentTxType === 'add' || currentTxType === 'remove') {
        setAmountA('');
        setAmountB('');
        setLpToRemove('');
      }

      // Always refetch allowances and balances after successful transaction
      if (refetchAllowanceA) refetchAllowanceA();
      if (refetchAllowanceB) refetchAllowanceB();
      if (refetchLpBalance) refetchLpBalance();
      if (refetchLpAllowance) refetchLpAllowance();

      // Reset transaction type
      setCurrentTxType(null);
    }
  }, [isSuccess, currentTxType, refetchAllowanceA, refetchAllowanceB, refetchLpBalance, refetchLpAllowance]);

  useEffect(() => {
    if (isReceiptError && txHash) {
      setStatus(`⚠️ Could not confirm receipt — check explorer:\n${EXPLORER}/tx/${txHash}`);
      setCurrentTxType(null);
    }
  }, [isReceiptError, txHash]);

  useEffect(() => {
    if (writeError) {
      console.error('[LIQUIDITY] Write error:', writeError);
      const msg = writeError.message || writeError.shortMessage || '';
      if (msg.includes('User rejected') || msg.includes('user rejected')) {
        setStatus('❌ Transaction rejected by user');
      } else if (msg.includes('Pausable: paused')) {
        setStatus('❌ Contract is paused — contact support');
      } else if (msg) {
        setStatus(`❌ ${msg.slice(0, 120)}`);
      } else {
        setStatus('❌ Transaction failed — check the explorer for details');
      }
      setCurrentTxType(null);
    }
  }, [writeError]);

  useEffect(() => {
    if (receiptError) {
      console.error('[LIQUIDITY] Receipt error:', receiptError);
      setStatus(`❌ Transaction failed: ${receiptError?.shortMessage || receiptError?.message || 'Unknown error'}`);
      setCurrentTxType(null);
    }
  }, [receiptError]);

  // Update LP balance display
  useEffect(() => {
    if (lpBalanceData !== undefined) {
      // Use formatUnits for display to avoid floating-point rounding errors
      const exact = formatUnits(lpBalanceData, 18);
      const num = parseFloat(exact);
      // Show in scientific notation if too small for 6 dp display
      setLpBalance(num > 0 && num < 0.000001 ? num.toExponential(4) : num.toFixed(6));
    }
  }, [lpBalanceData]);

  // Reset LP balance when pair address changes to invalid/null
  useEffect(() => {
    if (!pairAddress || pairAddress === '0x0000000000000000000000000000000000000000') {
      setLpBalance('0');
    }
  }, [pairAddress]);

  // Check if LP tokens need approval for removal
  useEffect(() => {
    if (lpToRemove && lpAllowance !== undefined) {
      try {
        const needed = parseUnits(lpToRemove, 18);
        setNeedsLpApproval(lpAllowance < needed);
      } catch {
        setNeedsLpApproval(false);
      }
    } else {
      setNeedsLpApproval(false);
    }
  }, [lpToRemove, lpAllowance]);

  // Check if Token A needs approval
  useEffect(() => {
    if (tokenA?.flare_contract && amountA && allowanceA !== undefined) {
      try {
        const needed = parseUnits(amountA, tokenA.decimals ?? 18);
        setNeedsApprovalA(allowanceA < needed);
      } catch {
        setNeedsApprovalA(false);
      }
    } else {
      setNeedsApprovalA(false);
    }
  }, [tokenA, amountA, allowanceA]);

  // Check if Token B needs approval
  useEffect(() => {
    if (tokenB?.flare_contract && amountB && allowanceB !== undefined) {
      try {
        const needed = parseUnits(amountB, tokenB.decimals ?? 18);
        setNeedsApprovalB(allowanceB < needed);
      } catch {
        setNeedsApprovalB(false);
      }
    } else {
      setNeedsApprovalB(false);
    }
  }, [tokenB, amountB, allowanceB]);

  // Update pair address when tokens change
  useEffect(() => {
    if (pairAddressData && pairAddressData !== '0x0000000000000000000000000000000000000000') {
      setPairAddress(pairAddressData);
    } else {
      setPairAddress(null);
      setReserves(null);
    }
  }, [pairAddressData]);

  // Update reserves when they change
  useEffect(() => {
    if (reservesData && token0Address && tokenA?.flare_contract && tokenB?.flare_contract) {
      const reserve0 = reservesData[0];
      const reserve1 = reservesData[1];

      // Determine which reserve corresponds to which token
      const isTokenAFirst = tokenA.flare_contract.toLowerCase() === token0Address.toLowerCase();

      setReserves({
        reserveA: isTokenAFirst ? reserve0 : reserve1,
        reserveB: isTokenAFirst ? reserve1 : reserve0,
      });
    } else {
      setReserves(null);
    }
  }, [reservesData, token0Address, tokenA, tokenB]);


  const loadTokens = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tokens/list?status=approved`);
      const data = await res.json();

      // Add "w" prefix to bridged token names for clarity
      // IMPORTANT: All wrapped tokens on Flare have 18 decimals (contract standard),
      // even though database stores XRPL native decimals for bridge scaling
      const wrappedTokens = (data.tokens || [])
        .filter(t => t.flare_contract)
        .map(t => ({
          ...t,
          symbol: `w${t.symbol}`,
          name: `Wrapped ${t.name}`,
          decimals: 18, // Override: all wrapped ERC20 tokens have 18 decimals on Flare
        }));

      const list = [
        { symbol: 'FLR', name: 'Flare (native)', flare_contract: null, decimals: 18 },
        { symbol: 'WFLR', name: 'Wrapped Flare', flare_contract: CONTRACT_ADDRESSES.WFLR, decimals: 18 },
        { symbol: 'EMBER', name: 'EmberX Token', flare_contract: CONTRACT_ADDRESSES.EMBER_TOKEN, decimals: 18 },
        ...wrappedTokens,
      ];
      setTokens(list);
      if (list.length >= 2) { setTokenA(list[0]); setTokenB(list[2]); }
    } catch (e) { console.error(e); }
  };

  // Format a calculated amount with enough precision to be non-zero
  const formatCalculatedAmount = (formattedValue, tokenDecimals) => {
    const num = parseFloat(formattedValue);
    if (isNaN(num) || num === 0) return '0';
    // If the value would round to 0 at 6 decimal places, use scientific notation
    if (num > 0 && num < 0.000001) {
      return num.toExponential(6);
    }
    const decimalPlaces = Math.min(tokenDecimals, 6);
    return num.toFixed(decimalPlaces);
  };

  // Check if the pool ratio is severely skewed (one token amount is extremely tiny)
  const isPoolRatioSkewed = (reserveA, reserveB, tokenADecimals, tokenBDecimals) => {
    if (!reserveA || !reserveB || reserveA === 0n || reserveB === 0n) return false;
    const normalizedA = Number(formatUnits(reserveA, tokenADecimals));
    const normalizedB = Number(formatUnits(reserveB, tokenBDecimals));
    if (normalizedA === 0 || normalizedB === 0) return true;
    const ratio = Math.max(normalizedA, normalizedB) / Math.min(normalizedA, normalizedB);
    // Flag as skewed if ratio exceeds 1 billion (indicative of a decimal bug)
    return ratio > 1_000_000_000;
  };

  const handleAmountAChange = (value) => {
    setAmountA(value);

    // Auto-calculate amount B if pool has liquidity
    if (value && reserves && reserves.reserveA > 0n && reserves.reserveB > 0n && tokenA && tokenB) {
      try {
        const tokenADecimals = (tokenA.decimals && tokenA.decimals > 0) ? tokenA.decimals : 18;
        const tokenBDecimals = (tokenB.decimals && tokenB.decimals > 0) ? tokenB.decimals : 18;
        const inputAmount = parseUnits(value, tokenADecimals);
        const calculatedB = (inputAmount * reserves.reserveB) / reserves.reserveA;
        const formattedB = formatUnits(calculatedB, tokenBDecimals);
        setAmountB(formatCalculatedAmount(formattedB, tokenBDecimals));
      } catch (e) {
        console.error('Error calculating amount B:', e);
      }
    } else if (!value) {
      setAmountB('');
    }
  };

  const handleAmountBChange = (value) => {
    setAmountB(value);

    // Auto-calculate amount A if pool has liquidity
    if (value && reserves && reserves.reserveA > 0n && reserves.reserveB > 0n && tokenA && tokenB) {
      try {
        const tokenADecimals = (tokenA.decimals && tokenA.decimals > 0) ? tokenA.decimals : 18;
        const tokenBDecimals = (tokenB.decimals && tokenB.decimals > 0) ? tokenB.decimals : 18;
        const inputAmount = parseUnits(value, tokenBDecimals);
        const calculatedA = (inputAmount * reserves.reserveA) / reserves.reserveB;
        const formattedA = formatUnits(calculatedA, tokenADecimals);
        setAmountA(formatCalculatedAmount(formattedA, tokenADecimals));
      } catch (e) {
        console.error('Error calculating amount A:', e);
      }
    } else if (!value) {
      setAmountA('');
    }
  };

  const handleApproveA = async () => {
    if (!tokenA?.flare_contract) return;
    try {
      setCurrentTxType('approveA');
      writeContract({
        address: tokenA.flare_contract,
        abi: EMBER_TOKEN_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESSES.ROUTER, maxUint256],
      });
      setStatus('Approving Token A...');
    } catch (e) {
      setStatus('Approval failed.');
      setCurrentTxType(null);
    }
  };

  const handleApproveB = async () => {
    if (!tokenB?.flare_contract) return;
    try {
      setCurrentTxType('approveB');
      writeContract({
        address: tokenB.flare_contract,
        abi: EMBER_TOKEN_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESSES.ROUTER, maxUint256],
      });
      setStatus('Approving Token B...');
    } catch (e) {
      setStatus('Approval failed.');
      setCurrentTxType(null);
    }
  };

  const handleAddLiquidity = async () => {
    if (!tokenA || !tokenB || !amountA || !amountB || notDeployed || !isConnected) return;
    const deadline = Math.floor(Date.now() / 1000) + 20 * 60;

    // Normalize amounts: parseUnits cannot handle scientific notation (e.g. "1e-15"),
    // so convert via toFixed(18) which always produces a plain decimal string.
    let parsedAmountA, parsedAmountB;
    try {
      const numA = parseFloat(amountA);
      const numB = parseFloat(amountB);
      if (isNaN(numA) || isNaN(numB) || numA < 0 || numB < 0) throw new Error('invalid');
      parsedAmountA = parseUnits(numA.toFixed(tokenA.decimals ?? 18), tokenA.decimals ?? 18);
      parsedAmountB = parseUnits(numB.toFixed(tokenB.decimals ?? 18), tokenB.decimals ?? 18);
    } catch {
      setStatus('Error: Invalid amounts entered.');
      return;
    }
    if (parsedAmountA === 0n || parsedAmountB === 0n) {
      setStatus('Error: One token amount is effectively zero. The pool price ratio is severely skewed. Remove existing liquidity first, then re-add with the correct ratio.');
      return;
    }

    const parsedA = parsedAmountA;
    const parsedB = parsedAmountB;
    const minA = parsedA * 95n / 100n;
    const minB = parsedB * 95n / 100n;

    const aIsNative = tokenA.symbol === 'FLR';
    const bIsNative = tokenB.symbol === 'FLR';

    try {
      setCurrentTxType('add');
      if (aIsNative) {
        // FLR + ERC-20: addLiquidityFLR(token, tokenDesired, tokenMin, flrMin, to, deadline)
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'addLiquidityFLR',
          args: [tokenB.flare_contract, parsedB, minB, minA, address, deadline],
          value: parsedA,
        });
      } else if (bIsNative) {
        // ERC-20 + FLR: addLiquidityFLR(token, tokenDesired, tokenMin, flrMin, to, deadline)
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'addLiquidityFLR',
          args: [tokenA.flare_contract, parsedA, minA, minB, address, deadline],
          value: parsedB,
        });
      } else {
        // ERC-20 + ERC-20
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'addLiquidity',
          args: [tokenA.flare_contract, tokenB.flare_contract, parsedA, parsedB, minA, minB, address, deadline],
        });
      }
      setStatus('Waiting for confirmation...');
    } catch (e) {
      setStatus('Failed to add liquidity.');
      setCurrentTxType(null);
    }
  };

  const handleApproveLp = async () => {
    if (!pairAddress || pairAddress === '0x0000000000000000000000000000000000000000') return;
    try {
      setCurrentTxType('approveLp');
      writeContract({
        address: pairAddress,
        abi: PAIR_ABI,
        functionName: 'approve',
        args: [CONTRACT_ADDRESSES.ROUTER, maxUint256],
      });
      setStatus('Approving LP tokens...');
    } catch (e) {
      setStatus('LP approval failed.');
      setCurrentTxType(null);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!lpToRemove || notDeployed || !isConnected || !tokenA || !tokenB) return;

    // Parse the LP amount, handling scientific notation via toFixed
    let parsedLp;
    try {
      const numLp = parseFloat(lpToRemove);
      if (isNaN(numLp) || numLp <= 0) {
        setStatus('Error: Amount must be greater than 0');
        return;
      }
      parsedLp = parseUnits(numLp.toFixed(18), 18);
    } catch (e) {
      setStatus('Invalid amount entered.');
      return;
    }

    // Cap at the actual raw balance to avoid rounding-up errors
    if (lpBalanceData && parsedLp > lpBalanceData) {
      parsedLp = lpBalanceData;
    }

    if (parsedLp === 0n) {
      setStatus('Error: Amount is zero after rounding.');
      return;
    }

    // Check for dust amounts that might cause issues
    if (parsedLp < 1000n) {
      setStatus('Error: Amount too small. Please enter a larger amount.');
      return;
    }

    const deadline = Math.floor(Date.now() / 1000) + 20 * 60;
    const minA = 0n; // Accept any amount (can be adjusted for slippage)
    const minB = 0n;

    const aIsNative = tokenA.symbol === 'FLR';
    const bIsNative = tokenB.symbol === 'FLR';

    try {
      setCurrentTxType('remove');
      if (aIsNative) {
        // FLR + ERC-20: removeLiquidityFLR(token, liquidity, tokenMin, flrMin, to, deadline)
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'removeLiquidityFLR',
          args: [tokenB.flare_contract, parsedLp, minB, minA, address, deadline],
        });
      } else if (bIsNative) {
        // ERC-20 + FLR: removeLiquidityFLR(token, liquidity, tokenMin, flrMin, to, deadline)
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'removeLiquidityFLR',
          args: [tokenA.flare_contract, parsedLp, minA, minB, address, deadline],
        });
      } else {
        // ERC-20 + ERC-20: removeLiquidity(tokenA, tokenB, liquidity, amountAMin, amountBMin, to, deadline)
        writeContract({
          address: CONTRACT_ADDRESSES.ROUTER,
          abi: ROUTER_ABI,
          functionName: 'removeLiquidity',
          args: [tokenA.flare_contract, tokenB.flare_contract, parsedLp, minA, minB, address, deadline],
        });
      }
      setStatus('Removing liquidity...');
    } catch (e) {
      setStatus('Failed to remove liquidity: ' + (e.message || 'Unknown error'));
      console.error('Remove liquidity error:', e);
      setCurrentTxType(null);
    }
  };

  const inputStyle = { width: '100%', padding: '12px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '1rem', boxSizing: 'border-box' };
  const selectStyle = { width: '100%', padding: '12px', backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', color: '#fff', fontSize: '1rem', cursor: 'pointer', marginTop: '6px', boxSizing: 'border-box' };

  return (
    <div className="content-section active">
      <div className="section">
        <div className="section-title">Liquidity Pools</div>

        {notDeployed && (
          <div style={{ backgroundColor: '#0f0520', border: '1px solid rgba(109, 40, 217, 0.5)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#C4B5FD', fontSize: '0.875rem', lineHeight: '1.6' }}>
            Contracts not yet deployed. Run: <code>cd contracts && npx hardhat run scripts/deploy.js --network flare</code>
          </div>
        )}

        {/* LP Balance Summary - Always Visible */}
        {tokenA && tokenB && (
          <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '4px' }}>
                  Your {tokenA.symbol}/{tokenB.symbol} LP Tokens
                </div>
                <div style={{ color: '#FFB84D', fontWeight: '700', fontSize: '1.5rem' }}>
                  {pairAddress ? parseFloat(lpBalance).toFixed(6) : '0.000000'} LP
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                {pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000' ? (
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Contract: {pairAddress.slice(0, 6)}...{pairAddress.slice(-4)}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.75rem', color: '#FFB84D' }}>
                    ⚠️ Pool not created yet
                  </div>
                )}
                {!isConnected && (
                  <div style={{ fontSize: '0.75rem', color: '#EF4444', marginTop: '4px' }}>
                    ⚠️ Wallet not connected
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {['add', 'remove'].map(panel => (
            <button key={panel} onClick={() => setActivePanel(panel)} style={{ flex: 1, padding: '10px', backgroundColor: activePanel === panel ? '#FB923C' : '#2a2a2a', color: activePanel === panel ? '#1A0800' : '#fff', border: '1px solid #3a3a3a', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', textTransform: 'capitalize' }}>
              {panel === 'add' ? 'Add Liquidity' : 'Remove Liquidity'}
            </button>
          ))}
        </div>

        {/* LP Rewards Panel - shown for EMBER/WFLR pair */}
        {pairAddress === CONTRACT_ADDRESSES.EMBER_WFLR_PAIR && (
          <LPRewardsPanel
            pairAddress={pairAddress}
            tokenA={tokenA}
            tokenB={tokenB}
          />
        )}

        {activePanel === 'add' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Token A</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                {tokenA && getTokenLogo(tokenA.symbol) && (
                  <img
                    src={getTokenLogo(tokenA.symbol)}
                    alt={tokenA.symbol}
                    style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #3a3a3a' }}
                  />
                )}
                <select style={{ ...selectStyle, marginTop: '0', flex: 1 }} value={tokenA?.symbol || ''} onChange={e => setTokenA(tokens.find(t => t.symbol === e.target.value))}>
                  {tokens.map(t => <option key={t.symbol} value={t.symbol}>{t.symbol} — {t.name}</option>)}
                </select>
              </div>
              <input style={{ ...inputStyle, marginTop: '8px' }} type="number" placeholder="Amount" value={amountA} onChange={e => handleAmountAChange(e.target.value)} />
            </div>

            <div style={{ textAlign: 'center', color: '#A78BFA', fontSize: '18px' }}>+</div>

            <div>
              <label style={{ color: '#9ca3af', fontSize: '0.875rem' }}>Token B</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                {tokenB && getTokenLogo(tokenB.symbol) && (
                  <img
                    src={getTokenLogo(tokenB.symbol)}
                    alt={tokenB.symbol}
                    style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2px solid #3a3a3a' }}
                  />
                )}
                <select style={{ ...selectStyle, marginTop: '0', flex: 1 }} value={tokenB?.symbol || ''} onChange={e => setTokenB(tokens.find(t => t.symbol === e.target.value))}>
                  {tokens.map(t => <option key={t.symbol} value={t.symbol}>{t.symbol} — {t.name}</option>)}
                </select>
              </div>
              <input style={{ ...inputStyle, marginTop: '8px' }} type="number" placeholder="Amount" value={amountB} onChange={e => handleAmountBChange(e.target.value)} />
            </div>

            <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '12px', fontSize: '0.875rem', color: '#9ca3af' }}>
              {reserves && reserves.reserveA > 0n && reserves.reserveB > 0n ? (
                <>
                  {isPoolRatioSkewed(reserves.reserveA, reserves.reserveB, tokenA?.decimals ?? 18, tokenB?.decimals ?? 18) ? (
                    <div style={{ color: '#EF4444', marginBottom: '8px', fontSize: '0.75rem', lineHeight: '1.5' }}>
                      ⚠️ Pool ratio is severely skewed — this pool was initialized with incorrect token amounts (likely a decimal bug).
                      The skewed ratio is <strong>permanently locked</strong> by minimum liquidity. Removing and re-adding liquidity will not fix it.
                      You can still add liquidity, but the router will enforce the current skewed ratio. The auto-calculated amounts below reflect this ratio.
                    </div>
                  ) : (
                    <div style={{ color: '#4ade80', marginBottom: '8px', fontSize: '0.75rem' }}>
                      ✓ Auto-calculating equal amounts based on pool ratio
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#FFB84D', marginBottom: '8px', fontSize: '0.75rem' }}>
                  ℹ First liquidity - you can set any ratio
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Pool share</span><span>—</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>LP tokens received</span><span>—</span>
              </div>
            </div>

            {needsApprovalA && (
              <button className="button" onClick={handleApproveA} disabled={!isConnected || isPending || isConfirming || notDeployed} style={{ backgroundColor: '#8B5CF6', marginBottom: '8px' }}>
                {isPending || isConfirming ? 'Approving...' : `Approve ${tokenA?.symbol}`}
              </button>
            )}

            {needsApprovalB && (
              <button className="button" onClick={handleApproveB} disabled={!isConnected || isPending || isConfirming || notDeployed} style={{ backgroundColor: '#8B5CF6', marginBottom: '8px' }}>
                {isPending || isConfirming ? 'Approving...' : `Approve ${tokenB?.symbol}`}
              </button>
            )}

            <button className="button" onClick={handleAddLiquidity} disabled={!isConnected || !amountA || !amountB || needsApprovalA || needsApprovalB || isPending || isConfirming || notDeployed}>
              {isPending || isConfirming ? 'Adding...' : !isConnected ? 'Connect Wallet' : needsApprovalA || needsApprovalB ? 'Approve tokens first' : 'Add Liquidity'}
            </button>
          </div>
        )}

        {activePanel === 'remove' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Warning if LP tokens are staked */}
            {hasStakedLp && isConnected && (
              <div style={{ backgroundColor: '#1a0d00', border: '2px solid rgba(251, 146, 60, 0.7)', borderRadius: '8px', padding: '16px', marginBottom: '8px', color: '#FED7AA', fontSize: '0.875rem', lineHeight: '1.6' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.5rem', marginRight: '12px' }}>⚠️</span>
                  <strong style={{ fontSize: '1rem', color: '#FB923C' }}>You have LP tokens staked in the Farm!</strong>
                </div>
                <div style={{ marginLeft: '0px' }}>
                  <strong>Staked Amount:</strong> {(Number(stakedLpAmount) / 1e18).toFixed(6)} LP tokens
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '6px', fontSize: '0.85rem' }}>
                    <strong style={{ color: '#FFB84D' }}>⚡ Important:</strong> You cannot remove liquidity for staked tokens.
                    <br/><br/>
                    <strong style={{ color: '#4ade80' }}>To withdraw your liquidity:</strong>
                    <ol style={{ marginTop: '8px', marginLeft: '20px', lineHeight: '1.8' }}>
                      <li>Go to the <strong>Farm</strong> tab</li>
                      <li>Click <strong>"Unstake LP"</strong> to withdraw your LP tokens</li>
                      <li>Return here to remove liquidity and get back your EMBER + FLR</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {!isConnected ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '8px' }}>🔌</div>
                Please connect your wallet to view and remove liquidity.
              </div>
            ) : Number(lpBalance) > 0 ? (
              <>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '0.875rem', display: 'block', marginBottom: '8px' }}>
                    Amount to Remove
                  </label>
                  <input
                    style={{
                      ...inputStyle,
                      borderColor: lpToRemove && parseFloat(lpToRemove) > parseFloat(lpBalance) ? '#EF4444' : '#3a3a3a'
                    }}
                    type="number"
                    placeholder="0.0"
                    value={lpToRemove}
                    onChange={e => {
                      const value = e.target.value;
                      setLpToRemove(value);
                      // Clear error status when user changes input
                      if (status.startsWith('Error:')) {
                        setStatus('');
                      }
                    }}
                    max={lpBalance}
                    step="any"
                  />
                  {lpToRemove && parseFloat(lpToRemove) > parseFloat(lpBalance) && (
                    <div style={{ color: '#EF4444', fontSize: '0.75rem', marginTop: '4px' }}>
                      ⚠️ Amount exceeds your balance
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <button
                      onClick={() => setLpToRemove(lpBalanceData ? formatUnits(lpBalanceData, 18) : lpBalance)}
                      style={{ padding: '6px 12px', backgroundColor: '#3a3a3a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}
                    >
                      MAX
                    </button>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      Balance: {lpBalance} LP
                    </span>
                  </div>
                </div>

                <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '12px', fontSize: '0.875rem', color: '#9ca3af' }}>
                  <div style={{ marginBottom: '4px' }}>You will receive:</div>
                  <div style={{ color: '#fff' }}>• {tokenA?.symbol || 'Token A'} tokens</div>
                  <div style={{ color: '#fff' }}>• {tokenB?.symbol || 'Token B'} tokens</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '8px' }}>
                    Exact amounts depend on current pool ratio
                  </div>
                </div>

                {needsLpApproval && (
                  <button
                    className="button"
                    onClick={handleApproveLp}
                    disabled={!isConnected || isPending || isConfirming || notDeployed}
                    style={{ backgroundColor: '#8B5CF6' }}
                  >
                    {isPending || isConfirming ? 'Approving...' : 'Approve LP Tokens'}
                  </button>
                )}

                <button
                  className="button"
                  onClick={handleRemoveLiquidity}
                  disabled={(() => {
                    if (!isConnected || !lpToRemove || isPending || isConfirming || notDeployed || needsLpApproval) return true;
                    const numLp = parseFloat(lpToRemove);
                    if (isNaN(numLp) || numLp <= 0) return true;
                    // Use raw BigInt comparison to avoid float rounding issues
                    if (lpBalanceData) {
                      try {
                        const parsed = parseUnits(numLp.toFixed(18), 18);
                        if (parsed > lpBalanceData) return true;
                      } catch { return true; }
                    }
                    return false;
                  })()}
                  style={{ backgroundColor: '#EF4444' }}
                >
                  {isPending || isConfirming ? 'Removing...' :
                   !isConnected ? 'Connect Wallet' :
                   needsLpApproval ? 'Approve LP tokens first' :
                   'Remove Liquidity'}
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '8px' }}>💧</div>
                No liquidity positions found.<br />
                <span style={{ fontSize: '0.875rem' }}>Add liquidity first to see your positions here.</span>
                {pairAddress && pairAddress !== '0x0000000000000000000000000000000000000000' && (
                  <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '8px', fontSize: '0.75rem', textAlign: 'left' }}>
                    <strong style={{ color: '#FFB84D' }}>Note:</strong> LP balance shown: {lpBalance}<br />
                    Contract: {pairAddress}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {status && (() => {
          const isOk  = status.includes('✅');
          const isErr = status.includes('❌') || status.includes('⚠️');
          const [headline, txLine] = status.split('\n');
          const isExplorerLine = txLine?.startsWith(EXPLORER);
          return (
            <div style={{
              marginTop: '16px',
              padding: '14px',
              backgroundColor: isOk ? '#0f3a1a' : isErr ? '#3a0f0f' : '#1a1a1a',
              border: `1px solid ${isOk ? '#4ade80' : isErr ? '#ef4444' : '#3a3a3a'}`,
              borderRadius: '8px',
              color: isOk ? '#4ade80' : isErr ? '#ef4444' : '#fff',
              fontSize: '0.95rem',
              fontWeight: '600',
              textAlign: 'center',
            }}>
              <div>{headline}</div>
              {txLine && isExplorerLine && (
                <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                  <a href={txLine} target="_blank" rel="noreferrer" style={{ color: '#A78BFA', wordBreak: 'break-all' }}>
                    View on explorer ↗
                  </a>
                </div>
              )}
              {txHash && !isExplorerLine && (
                <div style={{ marginTop: '6px', fontSize: '0.78rem', fontWeight: '400' }}>
                  <a href={`${EXPLORER}/tx/${txHash}`} target="_blank" rel="noreferrer" style={{ color: '#A78BFA', wordBreak: 'break-all' }}>
                    {txHash.slice(0, 10)}…{txHash.slice(-8)} ↗
                  </a>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default LiquiditySection;
